import "./index.css"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { StrictMode } from "react"
import { createRoot } from "react-dom/client"
import { ThemeProvider, createTheme } from "@mui/material/styles"
import App from "./App.tsx"
import { BrowserRouter } from "react-router-dom"
import { PicassoAuthProvider } from "./hooks/common/usePicassoAuth.tsx"
import { registerSW } from "virtual:pwa-register"
import { initSentry } from "./utils/sentry"

// Initialize Sentry as early as possible
initSentry()

// Clear localStorage on new deploy to prevent stuck tiles
const currentVersion = __APP_VERSION__
const storedVersion = localStorage.getItem('app_version')
if (storedVersion !== currentVersion) {
  const userToken = localStorage.getItem('userToken')
  const userRole = localStorage.getItem('userRole')
  const userId = localStorage.getItem('user_id')
  const userEmail = localStorage.getItem('userEmail')

  localStorage.clear()

  // Restore auth tokens so user stays logged in
  if (userToken) localStorage.setItem('userToken', userToken)
  if (userRole) localStorage.setItem('userRole', userRole)
  if (userId) localStorage.setItem('user_id', userId)
  if (userEmail) localStorage.setItem('userEmail', userEmail)

  localStorage.setItem('app_version', currentVersion)
}

// Suppress known React 18 + react-virtual flushSync warning
// This is a harmless warning from @tanstack/react-virtual when it syncs scroll state
// See: https://github.com/TanStack/virtual/issues/506
const originalError = console.error;
console.error = (...args) => {
  if (typeof args[0] === 'string' && args[0].includes('flushSync was called from inside a lifecycle')) {
    return;
  }
  originalError.call(console, ...args);
};

const theme = createTheme({
  palette: {
    primary: {
      main: "#1976d2", // Custom primary color
    },
    secondary: {
      main: "#dc004e", // Custom secondary color
    },
  },
})

// Register service worker for PWA
registerSW({
  immediate: true,
})

// Auto-reload on service worker update
navigator.serviceWorker?.addEventListener("controllerchange", () => {
  globalThis.location.reload()
})

// QueryClient is only used for non-Picasso functionality (e.g., pet context)
// Picasso components use their own backend caching system
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  },
})
createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ThemeProvider theme={theme}>
      <PicassoAuthProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
                  <App />
          </BrowserRouter>
        </QueryClientProvider>
      </PicassoAuthProvider>
    </ThemeProvider>
  </StrictMode>
)
